export type ToolFamily =
  | 'Transcripts'
  | 'Creator data'
  | 'Analytics'
  | 'Research'
  | 'Ads'
  | 'Reviews and markets'
  | 'More';

export interface ToolEntry {
  category: string;
  description: string;
  family: ToolFamily;
  glyph: string;
  title: string;
  to: string;
}

export interface ToolMetadata {
  description: string;
  title: string;
}

export const TOOL_FAMILIES: readonly ToolFamily[] = [
  'Transcripts',
  'Creator data',
  'Analytics',
  'Research',
  'Ads',
  'Reviews and markets',
  'More',
];

/**
 * The transcript slugs `TranscriptPage` serves from one component. They are ordinary
 * entries in `TOOLS` below; this list exists only so the mount test knows their slug
 * is derived from a prop rather than written out on a page.
 */
export const TOOL_PRESET_PATHS = [
  '/tools/tiktok-transcript',
  '/tools/youtube-transcript',
  '/tools/instagram-transcript',
] as const;

export const TOOLS: ToolEntry[] = [
  {
    to: '/tools/social-transcript', glyph: 'youtube', category: 'Video', family: 'Transcripts',
    title: 'Social Transcript',
    description: 'Extract spoken text from public TikTok, YouTube, Instagram, Facebook, and X videos.',
  },
  {
    to: '/tools/youtube-transcript', glyph: 'youtube', category: 'YouTube', family: 'Transcripts',
    title: 'YouTube Transcript',
    description: 'Paste any public YouTube video link and get the full spoken-word transcript.',
  },
  {
    to: '/tools/tiktok-transcript', glyph: 'tiktok', category: 'TikTok', family: 'Transcripts',
    title: 'TikTok Transcript',
    description: 'Paste any public TikTok video link and get the spoken words as plain text.',
  },
  {
    to: '/tools/instagram-transcript', glyph: 'instagram', category: 'Instagram', family: 'Transcripts',
    title: 'Instagram Transcript',
    description: 'Paste a public Reel or Instagram video post and get its spoken-word transcript.',
  },
  {
    to: '/tools/youtube-channel-transcripts', glyph: 'youtube', category: 'YouTube', family: 'Transcripts',
    title: 'YouTube Channel Transcripts',
    description: 'Collect available transcripts from the latest videos returned for a public channel.',
  },
  {
    to: '/tools/profile-viewer', glyph: 'anyapi', category: 'Profiles', family: 'Creator data',
    title: 'Profile Viewer',
    description: 'Look up a public creator profile through one normalized AnyAPI contract.',
  },
  {
    to: '/tools/snapchat-story-viewer', glyph: 'snapchat', category: 'Snapchat', family: 'Creator data',
    title: 'Snapchat Story Viewer',
    description: 'Inspect the public profile and current stories returned for a Snapchat creator.',
  },
  {
    to: '/tools/tiktok-audience-analyzer', glyph: 'tiktok', category: 'TikTok', family: 'Creator data',
    title: 'TikTok Audience Analyzer',
    description: 'View the latest public country distribution for a TikTok audience.',
  },
  {
    to: '/tools/username-checker', glyph: 'anyapi', category: 'Profiles', family: 'Creator data',
    title: 'Username Checker',
    description: 'Check public profile existence across supported networks and report likely availability.',
  },
  {
    to: '/tools/instagram-non-followers', glyph: 'instagram', category: 'Instagram', family: 'Creator data',
    title: 'Instagram Non-Followers',
    description: 'Compare official export files entirely in your browser. Files never leave the page.',
  },
  {
    to: '/tools/engagement-rate-calculator', glyph: 'anyapi', category: 'Calculator', family: 'Analytics',
    title: 'Engagement Rate Calculator',
    description: 'Calculate transparent Instagram, TikTok, or YouTube engagement from your sample.',
  },
  {
    to: '/tools/creator-earnings-calculator', glyph: 'coinmarketcap', category: 'Calculator', family: 'Analytics',
    title: 'Creator Earnings Calculator',
    description: 'Model creator revenue with visible inputs and editable assumptions.',
  },
  {
    to: '/tools/youtube-shorts-earnings', glyph: 'youtube', category: 'YouTube', family: 'Analytics',
    title: 'YouTube Shorts Earnings',
    description: 'Estimate Shorts revenue from observed views, posting cadence, and an editable RPM.',
  },
  {
    to: '/tools/twitch-earnings-calculator', glyph: 'twitch', category: 'Twitch', family: 'Analytics',
    title: 'Twitch Earnings Calculator',
    description: 'Use your private dashboard totals with visible subscription and bit assumptions.',
  },
  {
    to: '/tools/youtube-comment-analyzer', glyph: 'youtube', category: 'YouTube', family: 'Analytics',
    title: 'YouTube Comment Analyzer',
    description: 'Count questions and terms, rank threads, and inspect source-cited themes and sentiment.',
  },
  {
    to: '/tools/reddit-research', glyph: 'reddit', category: 'Reddit', family: 'Research',
    title: 'Reddit Research',
    description: 'Search public Reddit posts with explicit sort and timeframe controls.',
  },
  {
    to: '/tools/reddit-marketing', glyph: 'reddit', category: 'Reddit', family: 'Research',
    title: 'Reddit Marketing Planner',
    description: 'Turn product context and retrieved Reddit rows into a source-cited plan.',
  },
  {
    to: '/tools/brand-mention-checker', glyph: 'reddit', category: 'Listening', family: 'Research',
    title: 'Brand Mention Checker',
    description: 'Find public posts that mention a brand and keep the source rows visible.',
  },
  {
    to: '/tools/switch-signals', glyph: 'reddit', category: 'Intent', family: 'Research',
    title: 'Switch Signals',
    description: 'Find Reddit posts where people describe leaving or replacing a competitor.',
  },
  {
    to: '/tools/ask-social-data', glyph: 'anyapi', category: 'Router', family: 'Research',
    title: 'Ask Social Data',
    description: 'Route a plain-language question to one constrained social-data operation.',
  },
  {
    to: '/tools/facebook-ad-library', glyph: 'facebook', category: 'Meta', family: 'Ads',
    title: 'Facebook Ad Library',
    description: 'Search normalized public Meta ads by keyword and activity status.',
  },
  {
    to: '/tools/amazon-review-signals', glyph: 'amazon', category: 'Amazon', family: 'Reviews and markets',
    title: 'Amazon Review Signals',
    description: 'Inspect sample rating, verified-purchase, timing, and repeated-wording signals.',
  },
  {
    to: '/tools/app-review-analyzer', glyph: 'anyapi', category: 'App stores', family: 'Reviews and markets',
    title: 'App Review Analyzer',
    description: 'Compare normalized rating samples and recurring terms across app stores.',
  },
  {
    to: '/tools/stock-comparison', glyph: 'anyapi', category: 'Markets', family: 'Reviews and markets',
    title: 'Stock Comparison',
    description: 'Compare current normalized quote, volume, market-cap, and 52-week facts.',
  },
  {
    to: '/tools/naver-blog-analytics', glyph: 'naver', category: 'Naver', family: 'Reviews and markets',
    title: 'Naver Blog Analytics',
    description: 'Measure Naver Blog competition, freshness, concentration, and optional ranking from sourced rows.',
  },
  {
    to: '/tools/reddit-seo-finder', glyph: 'reddit', category: 'Reddit', family: 'More',
    title: 'Reddit SEO Finder',
    description: 'Find Reddit threads already ranking on Google for your buyer-intent keywords.',
  },
  {
    to: '/tools/x-non-followers', glyph: 'x-twitter', category: 'X', family: 'More',
    title: 'X Non-Followers',
    description: 'Find accounts you follow on X that do not follow you back.',
  },
];

export const TOOLS_INDEX_METADATA: ToolMetadata = {
  title: 'Free social data tools',
  description: 'Use free, no-login social research, transcript, creator, review, ad, and market tools built with AnyAPI.',
};

export const TOOL_METADATA: Readonly<Record<string, ToolMetadata>> = {
  'social-transcript': { title: 'Free Social Video Transcript Tool', description: 'Get a TikTok, YouTube, Instagram, Facebook, or X transcript from one free tool.' },
  'youtube-channel-transcripts': { title: 'YouTube Channel Transcript Workflow', description: "Find a channel's latest videos and open each one for free transcript extraction." },
  'profile-viewer': { title: 'Free Social Profile Viewer', description: 'View normalized public creator profiles from TikTok, Instagram, YouTube, and LinkedIn.' },
  'snapchat-story-viewer': { title: 'Free Snapchat Story Viewer', description: 'View public Snapchat profile and current story metadata without signing in.' },
  'tiktok-audience-analyzer': { title: 'TikTok Audience Analyzer', description: 'Analyze the latest public TikTok audience location sample through AnyAPI.' },
  'username-checker': { title: 'Free Social Username Checker', description: 'Find public profile links and likely availability for one username across social platforms.' },
  'instagram-non-followers': { title: 'Private Instagram Non-Followers Checker', description: 'Compare official Instagram export files locally without uploading them.' },
  'engagement-rate-calculator': { title: 'Free Engagement Rate Calculator', description: 'Calculate creator engagement from visible likes, comments, shares, and followers.' },
  'creator-earnings-calculator': { title: 'Free Creator Earnings Calculator', description: 'Estimate a sponsored-post range from audience size, views, and engagement.' },
  'youtube-shorts-earnings': { title: 'YouTube Shorts Earnings Calculator', description: "Estimate monthly Shorts ad revenue from a channel's latest public Shorts views and observed cadence." },
  'twitch-earnings-calculator': { title: 'Free Twitch Earnings Calculator', description: 'Estimate monthly Twitch income from subscriptions, bits, viewers, and stream hours.' },
  'youtube-comment-analyzer': { title: 'Free YouTube Comment Analyzer', description: 'Analyze public YouTube comments with deterministic metrics plus source-cited themes and sentiment.' },
  'reddit-research': { title: 'Free Reddit Research Tool', description: 'Search public Reddit posts by topic, competitor, or switching phrase with source-linked results.' },
  'reddit-marketing': { title: 'Reddit Marketing Planner', description: 'Build a sourced Reddit community research and marketing plan through AnyAPI.' },
  'brand-mention-checker': { title: 'Free Reddit Brand Mention Checker', description: 'Find recent public Reddit mentions for any brand or keyword with direct source links.' },
  'switch-signals': { title: 'Free Competitor Switch Signals Tool', description: 'Find source-linked Reddit posts from people discussing alternatives, leaving, and switching.' },
  'ask-social-data': { title: 'Ask Social Data in Plain English', description: 'Route one plain-language question to typed social data and normalized JSON through AnyAPI.' },
  'facebook-ad-library': { title: 'Free Facebook Ad Library Search', description: 'Search public Facebook ads by brand, keyword, country, and active status through AnyAPI.' },
  'amazon-review-signals': { title: 'Amazon Review Signals', description: 'Inspect Amazon review rating mix, verified-purchase share, timing clusters, and exact duplicate text without making a fake-review verdict.' },
  'app-review-analyzer': { title: 'App Review Analyzer', description: 'Compare App Store and Google Play rating distributions, recurring review words, and weekly review volume.' },
  'stock-comparison': { title: 'Stock Comparison', description: 'Compare current price, day range, 52-week range, volume, market cap, and market state for exact ticker symbols.' },
  'naver-blog-analytics': { title: 'Naver Blog Analytics', description: 'Measure visible Naver Blog competition, blogger concentration, result freshness, and your rank in the returned public sample.' },
  'reddit-seo-finder': { title: 'Reddit SEO Finder - find the threads already ranking for you', description: 'Free tool: paste your website and find the Reddit threads already ranking on Google for your buyer-intent keywords - the highest-leverage places to leave a comment. No login.' },
  'x-non-followers': { title: "Who doesn't follow you back on X? - Free X tool", description: "Free tool to find the accounts you follow on X (Twitter) that don't follow you back - sorted smallest first, with a link to each profile. No login for the profile peek." },
  'tiktok-transcript': { title: 'Free TikTok Transcript Generator - get any TikTok transcript', description: 'Free tool to get the full transcript of any TikTok video. Paste a link, copy or download the text - no login. Powered by the AnyAPI gateway.' },
  'youtube-transcript': { title: 'Free YouTube Transcript Extractor - get any video transcript', description: 'Free tool to extract the full transcript of any YouTube video. Paste a link, copy or download the text - no login. Powered by the AnyAPI gateway.' },
  'instagram-transcript': { title: 'Free Instagram Transcript Generator - any Reel or video', description: 'Free tool to transcribe any public Instagram Reel or video. Paste a link, copy or download the spoken text - no login. Powered by the AnyAPI gateway.' },
};
